import type { CSSProperties, ReactNode } from 'react';

import { cn } from '@/utils/cn';

import { Checkbox } from './Checkbox';
import { Icon } from './Icon';

export type SortDirection = 'asc' | 'desc';

export interface TableColumn<Row> {
  id: string;
  header: ReactNode;
  cell: (row: Row, index: number) => ReactNode;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  cellClassName?: string;
  headerClassName?: string;
  hidden?: boolean;
}

export interface TableSort {
  id: string;
  direction: SortDirection;
}

interface TableProps<Row> {
  columns: TableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
  emptyMessage?: ReactNode;
  loading?: boolean;
  /** Selection */
  selection?: {
    selectedIds: Set<string>;
    onChange: (ids: Set<string>) => void;
    isRowSelectable?: (row: Row) => boolean;
  };
  /** Sort */
  sort?: TableSort | null;
  onSortChange?: (sort: TableSort | null) => void;
  /** Click handler for the row body (excluding the checkbox cell). */
  onRowClick?: (row: Row) => void;
  /** Per-row trailing actions, shown on hover. */
  rowActions?: (row: Row) => ReactNode;
  /** Optional outer className applied to .table-wrap. */
  className?: string;
}

export function Table<Row>({
  columns,
  rows,
  rowKey,
  emptyMessage = 'No results',
  loading,
  selection,
  sort,
  onSortChange,
  onRowClick,
  rowActions,
  className,
}: TableProps<Row>) {
  const visibleColumns = columns.filter((c) => !c.hidden);

  const allKeys = rows.map((row, i) => rowKey(row, i));
  const selectableKeys = selection
    ? allKeys.filter((_, i) => selection.isRowSelectable?.(rows[i]) !== false)
    : [];
  const selectedHere = selectableKeys.filter((id) => selection?.selectedIds.has(id));
  const allChecked = selection && selectableKeys.length > 0 && selectedHere.length === selectableKeys.length;
  const someChecked = selection && selectedHere.length > 0 && !allChecked;

  const toggleAll = () => {
    if (!selection) return;
    const next = new Set(selection.selectedIds);
    if (allChecked) {
      selectableKeys.forEach((id) => next.delete(id));
    } else {
      selectableKeys.forEach((id) => next.add(id));
    }
    selection.onChange(next);
  };

  const toggleOne = (id: string) => {
    if (!selection) return;
    const next = new Set(selection.selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selection.onChange(next);
  };

  const handleSort = (col: TableColumn<Row>) => {
    if (!col.sortable || !onSortChange) return;
    if (sort?.id !== col.id) onSortChange({ id: col.id, direction: 'asc' });
    else if (sort.direction === 'asc') onSortChange({ id: col.id, direction: 'desc' });
    else onSortChange(null);
  };

  return (
    <div className={cn('table-wrap', className)} aria-busy={loading || undefined}>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              {selection && (
                <th style={{ width: 36 }}>
                  <Checkbox
                    checked={!!allChecked}
                    indeterminate={!!someChecked}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {visibleColumns.map((col) => {
                const align = col.align ?? 'left';
                const isSorted = sort?.id === col.id;
                const headerStyle: CSSProperties = {
                  width: col.width,
                  textAlign: align,
                };
                return (
                  <th key={col.id} className={col.headerClassName} style={headerStyle}>
                    {col.sortable ? (
                      <button type="button" className="th-inner" onClick={() => handleSort(col)}>
                        {col.header}
                        <Icon
                          name={
                            isSorted
                              ? sort.direction === 'asc'
                                ? 'chevronUp'
                                : 'chevronDown'
                              : 'chevronUpDown'
                          }
                          size={11}
                          style={{ opacity: isSorted ? 1 : 0.45 }}
                        />
                      </button>
                    ) : (
                      <span className="th-inner" style={{ cursor: 'default' }}>
                        {col.header}
                      </span>
                    )}
                  </th>
                );
              })}
              {rowActions && <th style={{ width: 90 }} aria-label="Row actions" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0)}
                  className="table-empty"
                >
                  {loading ? 'Loading...' : emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const id = rowKey(row, i);
                const isSelected = selection?.selectedIds.has(id) ?? false;
                return (
                  <tr
                    key={id}
                    className={cn(isSelected && 'selected')}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{ cursor: onRowClick ? 'pointer' : undefined }}
                  >
                    {selection && (
                      <td onClick={(e) => e.stopPropagation()} style={{ width: 36 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleOne(id)}
                          disabled={selection.isRowSelectable?.(row) === false}
                          aria-label={`Select row ${i + 1}`}
                        />
                      </td>
                    )}
                    {visibleColumns.map((col) => (
                      <td
                        key={col.id}
                        className={col.cellClassName}
                        style={{ textAlign: col.align ?? 'left', width: col.width }}
                      >
                        {col.cell(row, i)}
                      </td>
                    ))}
                    {rowActions && (
                      <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
                        <div className="row-actions">{rowActions(row)}</div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
